import {all, call, put, takeLatest} from '@redux-saga/core/effects';
import accountApi from 'Agent/Accounts/api/accountApi';
import {
  changeCustomerConnectionProfile,
  CustomerConnectionProfile,
  A360EnrollmentApplicationActionType,
  showError,
  uploadApplication,
  getCustomerConnectionProfile,
  showDuplicateUploadError,
} from 'Agent/Accounts/ducks/a360EnrollmentApplication';
import {hideModal, ModalState, showModal} from 'Core/ducks/modal';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, Loaded, NoContent, ServerError} from 'Core/models/Async';
import {isRight} from 'fp-ts/lib/Either';

const isValidProfile = (profile: CustomerConnectionProfile): boolean =>
  profile.connectionProfileNumber != null &&
  profile.techPartnerName != null &&
  profile.fileNameConfiguration != null;

function* fetchCustomerConnectionProfileData(action: ReturnType<typeof getCustomerConnectionProfile>) {
  try {
    const response: AsyncResult<CustomerConnectionProfile[]> = yield call(
      accountApi.fetchCustomerConnectionProfile,
      action.payload.customerUri
    );
    if (isRight(response)) {
      const validProfiles: CustomerConnectionProfile[] = (response.right ?? []).filter(isValidProfile);
      if (validProfiles.length === 0) {
        yield put(createSnackbar('Enrollment upload is not available. Contact your representative at The Standard to establish a connection profile.'));
        return;
      }
      yield put(changeCustomerConnectionProfile(new Loaded(validProfiles)));
      yield put(showModal(ModalState.UploadA360EnrollmentApplication({
        accountNumber: action.payload.accountNumber,
        accountName: action.payload.accountName
      })));
    } else { // If api fails
      if (response.left?.key === 'NoContent') { // 204 - empty response, no connection profile configured
        yield put(createSnackbar('Enrollment upload is not available. Contact your representative at The Standard to establish a connection profile.'));
      } else {
        yield put(createSnackbar('An error occurred, please try again later or contact your support rep if error persists'));
      }
    }
  } catch (err) {
    console.error(err.message);
    yield put(createSnackbar('An error occurred, please try again later or contact your support rep if error persists'));
    //yield put(changeCustomerConnectionProfile(new ServerError(err.message)));
  }
}

export function* customerConnectionProfileSaga() {
  yield takeLatest(A360EnrollmentApplicationActionType.CUSTOMER_CONNECTION_PROFILE, fetchCustomerConnectionProfileData);
}

export function* uploadEnrollmentApplicationApi(action: ReturnType<typeof uploadApplication>) {
  try {
    const result: AsyncResult<string> = yield call(
      accountApi.uploadA360EnrollmentApplication,
      action.payload.accountNumber,
      action.payload.conProfileNo,
      action.payload.application.file
    );

    if (isRight(result)) {
      yield put(hideModal());

      if (action.payload.SIC_FileNet_Connectivity) {
        yield put(createSnackbar('Virus scan was successful and the document was uploaded.'));
      } else if (action.payload.enable_portals_virus_scan) {
        yield put(
          createSnackbar(
            'Document received, virus scan in progress; If a virus is detected in your document, we will notify you at the email on file. You can also check the status of your uploads here: ',
            true,
            'Document Upload History',
            '/agent/document-upload-history'
          )
        );
      } else {
        yield put(createSnackbar('Application Uploaded Successfully'));
      } 
    } else {
      if (action.payload.SIC_FileNet_Connectivity) {
        yield put(hideModal());
        yield put(createSnackbar('Virus scan failed and the document was not uploaded.'));
      } else if (result.left?.errorDetails?.key === 'DuplicateFileUpload') {
        yield put(showDuplicateUploadError(action.payload.application));
      } else {
        yield put(showError(action.payload.application));
      }
    }
  } catch (err) {
    console.error(err.message);
    yield put(createSnackbar(err.message));
  }
}

export function* uploadEnrollmentApplicationSaga() {
  yield takeLatest(A360EnrollmentApplicationActionType.UPLOAD_APPLICATION, uploadEnrollmentApplicationApi);
}

export default function*() {
  yield all([
    customerConnectionProfileSaga(),
    uploadEnrollmentApplicationSaga()
  ]);
}
